import { useEffect, useRef, useId, ReactNode, RefObject, MouseEvent } from 'react';
import styles from './DrillDownDrawer.module.css';

export type DrawerSide = 'start' | 'end';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'full';

export interface DrillDownDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** מוצג ב-header של ה-drawer + aria-labelledby */
  title: string;
  /** תוכן ה-drawer */
  children: ReactNode;
  /** איזה צד נפתח. 'start' = ימין ב-RTL (default); 'end' = שמאל ב-RTL */
  side?: DrawerSide;
  /** רוחב: 320 / 480 / 640 / 100% */
  size?: DrawerSize;
  closeOnEscape?: boolean;
  closeOnOverlayClick?: boolean;
  /** אם קיים, ה-element יקבל focus בפתיחה */
  initialFocusRef?: RefObject<HTMLElement>;
  className?: string;
}

/** elements שניתן לקבל focus עבור focus trap */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes.filter(Boolean).join(' ');

/**
 * Side drawer ל-drill-down (R8). נפתח מצד `start` (ימין ב-RTL) בברירת מחדל.
 *
 * **ARIA pattern:** `role="dialog"` + `aria-modal="true"` + `aria-labelledby` → title h2.
 *
 * **Focus management:**
 * 1. בפתיחה — שומר את ה-active element הקודם
 * 2. focus עובר ל-`initialFocusRef` אם סופק, אחרת ל-close button
 * 3. Tab/Shift+Tab בתוך drawer מסתובב (focus trap)
 * 4. בסגירה — focus חוזר ל-element הקודם
 *
 * @example
 * <DrillDownDrawer
 *   isOpen={detailOpen}
 *   onClose={() => setDetailOpen(false)}
 *   title="פירוט הכנסות מאי 2026"
 *   size="lg"
 * >
 *   <RevenueDetailTable rows={detailRows} />
 * </DrillDownDrawer>
 */
export const DrillDownDrawer = ({
  isOpen,
  onClose,
  title,
  children,
  side = 'start',
  size = 'md',
  closeOnEscape = true,
  closeOnOverlayClick = true,
  initialFocusRef,
  className,
}: DrillDownDrawerProps): JSX.Element | null => {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const lastFocusedRef = useRef<Element | null>(null);
  const titleId = useId();

  // Save previously-focused element + auto-focus on open
  useEffect(() => {
    if (!isOpen) return undefined;

    lastFocusedRef.current = document.activeElement;
    const focusTarget = initialFocusRef?.current || closeBtnRef.current;
    if (focusTarget) {
      const id = window.requestAnimationFrame(() => focusTarget.focus());
      return () => window.cancelAnimationFrame(id);
    }
    return undefined;
  }, [isOpen, initialFocusRef]);

  // Restore focus on close
  useEffect(() => {
    if (isOpen) return undefined;
    const last = lastFocusedRef.current as HTMLElement | null;
    if (last && typeof last.focus === 'function') {
      last.focus();
      lastFocusedRef.current = null;
    }
    return undefined;
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus trap — Tab / Shift+Tab בתוך drawer
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab' || !drawerRef.current) return;
      const focusables = drawerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget && closeOnOverlayClick) onClose();
  };

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      data-testid="drill-down-drawer-overlay"
    >
      <div
        ref={drawerRef}
        className={cx(
          styles.drawer,
          styles[`side-${side}`],
          styles[`size-${size}`],
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="drill-down-drawer"
      >
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="סגור"
            data-testid="drill-down-drawer-close"
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className={styles.body} data-testid="drill-down-drawer-body">
          {children}
        </div>
      </div>
    </div>
  );
};
